/**
 * InvestigationWork: the Work section of the investigation DetailCard (#672).
 *
 * Reads `GET /api/v1/investigations/{id}/work` (#681): each work requirement with
 * the corroboration episode it ran and its result line (#670). Newest first.
 *
 * Tobias's interaction decisions (2026-09-30, #672 comment 5914536126):
 * - Check now is prefilled from the latest requirement and editable before it
 *   runs; every run is a new attempt and earlier attempts are kept as they were.
 * - The selection is explained in plain words; Jev's returned probabilities
 *   appear only under the collapsed "Technical details".
 * - No "Add as evidence": retrieved items are shown with their provenance
 *   (source, rule, retrieval time) and never change evidence counts.
 * - DEMO / REAL badges for the work context and for the observations are
 *   separate and never merged.
 *
 * Check now is shown only when the backend reports `check_enabled`
 * (`INVESTIGATION_WORK_CHECK_ENABLED=1`), which is off by default (D15 gate).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import {
  BASIS_OPTIONS,
  relevanceLine,
  selectionExplanation,
  type CheckForm,
  type DataLabel,
  type EpisodeRecord,
  type RelevanceBasis,
  type WorkItem,
  type WorkResponse,
} from './investigationWorkWording';


function sourceOf(record: EpisodeRecord | null): string | null {
  return record?.independence?.upstream ?? record?.chosen ?? null;
}

function timeAgo(iso: string | undefined, now = Date.now()): string {
  if (!iso) return '';
  const minutes = Math.round((now - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString();
}

function errorDetail(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'The check could not be run.';
}

// ── Section ──────────────────────────────────────────────────────────────────

export function InvestigationWork({ investigationId }: { investigationId: string }) {
  const queryClient = useQueryClient();
  const queryKey = ['investigation-work', investigationId];
  const [form, setForm] = useState<CheckForm | null>(null);

  const { data, isLoading, isError } = useQuery<WorkResponse>({
    queryKey,
    queryFn: async () => (await apiClient.get<WorkResponse>(`/api/v1/investigations/${investigationId}/work`)).data,
  });
  const work = Array.isArray(data?.work) ? data.work : [];
  const latestChecked = [...work].reverse().find((w) => w.episode?.cluster);
  // D15: Check now runs on demo work only; the backend refuses anything else.
  const canCheck = Boolean(data?.check_enabled && latestChecked?.data_label === 'demo');

  const check = useMutation({
    mutationFn: async (body: CheckForm) => {
      const payload = { ...body, relevance_link: body.relevance_link.trim() || null };
      return (await apiClient.post(`/api/v1/investigations/${investigationId}/work`, payload)).data;
    },
    onSuccess: () => {
      setForm(null);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  function openForm() {
    if (!latestChecked) return;
    check.reset();
    setForm({
      why_it_matters: latestChecked.why_it_matters,
      question: latestChecked.question,
      required_evidence: latestChecked.required_evidence,
      useful_result: latestChecked.useful_result,
      relevance_basis: latestChecked.relevance.basis,
      relevance_link: latestChecked.relevance.link ?? '',
    });
  }

  return (
    <div data-testid="investigation-work">
      <div className="flex items-center justify-between mb-2.5">
        <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--e-text-secondary)]">Work</div>
        {canCheck && (
          <button
            onClick={openForm}
            disabled={check.isPending || form !== null}
            className="font-mono text-[11px] tracking-[0.06em] px-3 py-1.5 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ border: '1px solid #14b8a6', color: '#14b8a6' }}
            data-testid="work-check-now"
          >
            {check.isPending ? 'Checking…' : 'Check now'}
          </button>
        )}
      </div>

      {data?.check_enabled && !canCheck && work.length > 0 && (
        <p className="font-mono text-[10.5px] text-[var(--e-text-muted)] mb-2" role="note">
          {latestChecked
            ? 'Check now runs on demo work only.'
            : 'Check now needs an earlier check that names a report.'}
        </p>
      )}

      {form && (
        <CheckNowForm
          form={form}
          onChange={setForm}
          pending={check.isPending}
          error={check.isError ? errorDetail(check.error) : null}
          onSubmit={() => check.mutate(form)}
          onCancel={() => setForm(null)}
        />
      )}

      {check.isPending && check.variables && (
        <div
          className="px-3.5 py-3 rounded-md mb-2"
          style={{ border: '1px dashed var(--e-env-border)', background: 'var(--e-env-bg-panel)' }}
          role="status"
          data-testid="work-pending"
        >
          <div className="text-[13px] text-[var(--e-text-primary)] leading-snug">{check.variables.question}</div>
          <div className="font-mono text-[11px] text-[var(--e-text-muted)] mt-1">
            Checking… this runs as a new attempt; earlier attempts are kept.
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-[12px] text-[var(--e-text-muted)] m-0">Loading work…</p>
      ) : isError ? (
        <p className="text-[12px] text-[var(--e-text-muted)] m-0">Work could not be loaded.</p>
      ) : work.length === 0 ? (
        <p className="text-[13.5px] text-[var(--e-text-muted)] m-0">No work formed for this investigation yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {[...work].reverse().map((w) => (
            <WorkCard key={w.id} work={w} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Badges ───────────────────────────────────────────────────────────────────

function LabelBadge({ label, kind }: { label: DataLabel; kind: 'context' | 'observations' }) {
  const demo = label === 'demo';
  return (
    <span
      className="font-mono text-[9.5px] uppercase tracking-[0.1em] px-2 py-[2px] rounded-full"
      style={{
        border: `1px solid ${demo ? 'oklch(0.688 0.165 72)' : 'oklch(0.658 0.148 195)'}`,
        color: demo ? 'oklch(0.760 0.150 72)' : 'oklch(0.720 0.130 195)',
      }}
      data-testid={`work-badge-${kind}`}
    >
      {demo ? 'DEMO' : 'REAL'} {kind}
    </span>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────

function WorkCard({ work }: { work: WorkItem }) {
  const [open, setOpen] = useState(false);
  const record = work.episode;
  const source = sourceOf(record);
  const rule = work.presentation?.details.rule ?? null;
  const observations = record?.cluster?.data_label;

  return (
    <div
      className="rounded-md px-3.5 py-3"
      style={{ border: '1px solid var(--e-env-border-subtle)', background: 'var(--e-env-bg-panel)' }}
      data-testid="work-card"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <LabelBadge label={work.data_label} kind="context" />
        {observations && <LabelBadge label={observations} kind="observations" />}
        <span className="flex-1" />
        <span className="font-mono text-[10px] text-[var(--e-text-muted)]">{timeAgo(work.created_at)}</span>
      </div>
      <div className="text-[13px] text-[var(--e-text-primary)] leading-snug">{work.question}</div>
      <div className="font-mono text-[10.5px] text-[var(--e-text-muted)] mt-1" data-testid="work-relevance">
        {relevanceLine(work.relevance)}
      </div>
      <div className="font-mono text-[11px] text-[var(--e-text-secondary)] mt-1" data-testid="work-result">
        {work.presentation ? work.presentation.summary : 'Not checked yet'}
        {source && ` · ${source}`}
        {rule && ` · rule ${rule.id}`}
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="font-mono text-[10.5px] text-[#14b8a6] hover:underline mt-2"
      >
        {open ? 'Hide what happened' : 'What happened'}
      </button>
      {open && <WhatHappened work={work} />}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="pt-2.5 mt-2.5" style={{ borderTop: '1px solid var(--e-env-border-subtle)' }}>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-[var(--e-text-muted)] mb-1">{label}</div>
      <div className="text-[12px] leading-relaxed text-[var(--e-text-secondary)]">{children}</div>
    </div>
  );
}

function WhatHappened({ work }: { work: WorkItem }) {
  const record = work.episode;
  const details = work.presentation?.details;
  const selection = record?.selection;
  const fetch = record?.fetch;
  return (
    <div data-testid="work-what-happened">
      <Row label="Available">
        {record?.shortlist?.length || record?.ineligible?.length ? (
          <ul className="m-0 pl-0 list-none flex flex-col gap-1">
            {record?.shortlist?.map((s) => (
              <li key={s.capability}>
                <span className="text-[var(--e-text-primary)]">{s.capability}</span>: {s.reason}
              </li>
            ))}
            {record?.ineligible?.map((s) => (
              <li key={s.capability} className="opacity-50" data-testid="work-ineligible">
                {s.capability}, not eligible: {s.reason}
              </li>
            ))}
          </ul>
        ) : (
          'No source was declared for this kind of report.'
        )}
      </Row>

      <Row label="Chosen">
        <p className="m-0" data-testid="work-selection">{selectionExplanation(record)}</p>
        {selection && (
          <details className="mt-1.5" data-testid="work-technical-details">
            <summary className="font-mono text-[10px] text-[var(--e-text-muted)] cursor-pointer">Technical details</summary>
            <div className="font-mono text-[10.5px] text-[var(--e-text-muted)] mt-1 flex flex-col gap-0.5">
              <span>Chosen by: {selection.by === 'jev' ? 'Jev' : 'declared order'}</span>
              {selection.confidence != null && <span>Selection confidence: {selection.confidence.toFixed(2)}</span>}
              {selection.probabilities &&
                Object.entries(selection.probabilities).map(([option, p]) => (
                  <span key={option}>
                    P({option}) = {p.toFixed(2)}
                  </span>
                ))}
              {record?.judgement?.reportable != null && <span>Reportable: {record.judgement.reportable.toFixed(2)}</span>}
              {record?.judgement?.model && <span>Model: {record.judgement.model}</span>}
              {selection.provenance?.question_set && <span>Question set: {selection.provenance.question_set}</span>}
              {selection.provenance?.schema_version && <span>Schema: {selection.provenance.schema_version}</span>}
              <span>Descriptive only; not a threshold.</span>
            </div>
          </details>
        )}
      </Row>

      <Row label="What happened">
        <p className="m-0 text-[var(--e-text-primary)]">{work.presentation?.summary ?? 'Not checked yet.'}</p>
        {fetch && fetch.returned != null && (
          <p className="m-0">
            {details?.evidence.length ?? 0} matched of {fetch.returned} returned
            {details?.rule && `, ${details.rule.text} (rule ${details.rule.id})`}.
          </p>
        )}
        {details && details.evidence.length > 0 && (
          <ul className="m-0 mt-1 pl-0 list-none flex flex-col gap-1" data-testid="work-retrieved">
            {/* Up to 3 retrieved items, as proposed on #672; the count above gives the total. */}
            {details.evidence.slice(0, 3).map((e) => (
              <li key={e.ref} className="font-mono text-[10.5px]">
                <span className="text-[var(--e-text-primary)]">{e.ref}</span>
                {e.title && ` · ${e.title}`}
                {` · ${e.source}`}
                {e.when && ` · observed ${new Date(e.when).toLocaleString()}`}
                {e.distance_km != null && ` · ${e.distance_km} km away`}
              </li>
            ))}
          </ul>
        )}
        {details && details.evidence.length > 3 && (
          <p className="m-0 font-mono text-[10.5px] text-[var(--e-text-muted)]">
            Showing 3 of {details.evidence.length} matched items.
          </p>
        )}
        {/* The attempt's time is the `now` its episode ran with, so it is when the items were retrieved. */}
        {work.created_at && fetch && (
          <p className="m-0 font-mono text-[10.5px] text-[var(--e-text-muted)]">
            Retrieved during the check at {new Date(work.created_at).toLocaleString()}
          </p>
        )}
        {details && (
          <>
            <p className="m-0 mt-1">Shows: {details.limitations.establishes}</p>
            <p className="m-0">Does not show: {details.limitations.does_not_establish}</p>
            {details.limitations.dependencies && <p className="m-0">{details.limitations.dependencies}.</p>}
          </>
        )}
      </Row>

      <Row label="Next">
        <p className="m-0">Wanted: {work.useful_result}</p>
        <p className="m-0 text-[var(--e-text-primary)]" data-testid="work-next">
          {work.presentation
            ? `Against it: ${work.presentation.summary}. ${work.presentation.details.limitations.does_not_establish}`
            : 'Against it: nothing yet; this requirement has not been checked.'}
        </p>
      </Row>
    </div>
  );
}

// ── Check now form ───────────────────────────────────────────────────────────

const FIELDS: Array<[keyof CheckForm, string]> = [
  ['question', 'Question'],
  ['why_it_matters', 'Why it matters'],
  ['required_evidence', 'Required evidence'],
  ['useful_result', 'Useful result'],
];

function CheckNowForm({
  form,
  onChange,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  form: CheckForm;
  onChange: (f: CheckForm) => void;
  pending: boolean;
  error: string | null;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const needsLink = form.relevance_basis === 'explicit_user_request';
  const complete =
    FIELDS.every(([k]) => form[k].trim()) && (!needsLink || form.relevance_link.trim().length > 0);
  const inputStyle = { border: '1px solid var(--e-env-border)', background: 'var(--e-env-bg-app)' };
  return (
    <form
      className="flex flex-col gap-2 px-3.5 py-3 rounded-md mb-2"
      style={{ border: '1px solid var(--e-env-border)' }}
      data-testid="work-check-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (complete && !pending) onSubmit();
      }}
    >
      <p className="m-0 font-mono text-[10.5px] text-[var(--e-text-muted)]">
        Prefilled from the latest check. Running creates a new attempt; earlier attempts are kept.
      </p>
      {FIELDS.map(([key, label]) => (
        <label key={key} className="flex flex-col gap-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--e-text-muted)]">{label}</span>
          <textarea
            value={form[key]}
            onChange={(e) => onChange({ ...form, [key]: e.target.value })}
            rows={2}
            className="text-[12px] text-[var(--e-text-primary)] rounded px-2 py-1.5"
            style={inputStyle}
          />
        </label>
      ))}
      <label className="flex flex-col gap-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--e-text-muted)]">Relevance basis</span>
        <select
          value={form.relevance_basis}
          onChange={(e) => onChange({ ...form, relevance_basis: e.target.value as RelevanceBasis })}
          className="text-[12px] text-[var(--e-text-primary)] rounded px-2 py-1.5"
          style={inputStyle}
        >
          {BASIS_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {needsLink && (
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--e-text-muted)]">
            Link to the request (required)
          </span>
          <input
            value={form.relevance_link}
            onChange={(e) => onChange({ ...form, relevance_link: e.target.value })}
            className="text-[12px] text-[var(--e-text-primary)] rounded px-2 py-1.5"
            style={inputStyle}
          />
        </label>
      )}
      {error && (
        <p className="m-0 font-mono text-[10.5px]" style={{ color: 'oklch(0.700 0.170 22)' }} role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!complete || pending}
          className="font-mono text-[11px] px-3 py-1.5 rounded disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ border: '1px solid #14b8a6', color: '#14b8a6' }}
          data-testid="work-check-run"
        >
          {pending ? 'Checking…' : 'Run check'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="font-mono text-[11px] px-3 py-1.5 rounded text-[var(--e-text-muted)] disabled:opacity-40"
          style={{ border: '1px solid var(--e-env-border)' }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
